import resourceSubject from 'next/javascripts/reactivity/resource-subject';
import { throttle } from 'next/javascripts/modules/utils';
import eventListenerMixin from 'next/javascripts/mixins/event-listener-mixin';

export default {
  mixins: [eventListenerMixin],

  properties: {
    stickyColumnMinWidth: {
      type: Number,
      default: 460,
    },

    headerSticky: {
      type: Boolean,
      default: true,
      observer() {
        this.adjustHeaderScrollLeft();
      },
    },
  },

  connected() {
    this.initVerticalScroll();
    this.initHorizontalScroll();
    this.initHeader();
  },

  ready() {
    this.updateHeaderStickyCellWidth(this.getStickyColumnMinWidth());
    this.initHorizontalScrollBarSimulator();
  },

  broadcastSubjectEvent(event, payload) {
    resourceSubject('tr-grid').next(event, { grid: this, ...payload });
  },

  initVerticalScroll() {
    this.verticalContainer = this.querySelector('.grid-vertical-scrollable');

    this.listenTo(this.verticalContainer, 'scroll', () => {
      if (this.header.classList.contains('horizontal-scrolling')) {
        this.headerStickyCellStyle.top = '';
        this.headerContentStyle.top = `${this.getVerticalContainerTop()}px`;
        this.header.classList.remove('horizontal-scrolling');
        this.adjustHeaderScrollLeft();
      }
    });

    this.listenTo(this.verticalContainer, 'scroll', throttle(() => {
      const headerRect = this.header.getBoundingClientRect();
      const headerSticky = headerRect.top < this.getVerticalContainerTop();

      this.headerContentStyle
        .top = headerSticky ? `${this.getVerticalContainerTop()}px` : '';
      this.headerSticky = headerSticky;
    }, 100));
  },

  initHorizontalScroll() {
    this.horizontalContainer = this.querySelector('.grid-horizontal-scrollable');
    this.horizontalScrollSeam = this.querySelector('.grid-horizontal-scroll-seam');

    this.listenTo(this.horizontalContainer, 'scroll', () => {
      if (this.headerSticky
        && !this.header.classList.contains('horizontal-scrolling')) {
        this.headerStickyCellStyle.top = `${this.getVerticalContainerTop()}px`;
        this.headerContentStyle.top = `${this.verticalContainer.scrollTop}px`;
        this.header.classList.add('horizontal-scrolling');
        this.adjustHeaderScrollLeft();
      }
      this.horizontalScrollSeam.classList
        .toggle('show', this.horizontalContainer.scrollLeft > 0);
    });
  },

  initHorizontalScrollBarSimulator() {
    const scrollBar = this.querySelector('.grid-horizontal-scroll-bar');
    if (!scrollBar) return false;

    const scrollBarContentStyle = this.querySelector('.grid-horizontal-scroll-bar-content').style;

    const resize = () => {
      scrollBar.style.width = `${this.horizontalContainer.clientWidth}px`;
      scrollBarContentStyle.width = `${this.horizontalContainer.scrollWidth}px`;
    };

    this.listenTo(window, 'resize', throttle(resize.bind(this), 100));

    // 延迟原因：scrollbar 在 ready 后才绘制，取 clientWidth 才是减去了它的宽度
    setTimeout(() => resize(), 0);

    this.listenTo(scrollBar, 'scroll', throttle(() => {
      this.horizontalContainer.scrollLeft = scrollBar.scrollLeft;
    }, 50));

    this.listenTo(this.horizontalContainer, 'scroll', throttle(() => {
      scrollBar.scrollLeft = this.horizontalContainer.scrollLeft;
    }, 50));

    this.listenTo(this.verticalContainer, 'scroll', throttle(() => {
      const { scrollTop, offsetHeight, scrollHeight } = this.verticalContainer;
      const isBottom = scrollTop + offsetHeight >= scrollHeight;

      if (!isBottom) {
        scrollBar.scrollLeft = this.horizontalContainer.scrollLeft;
      }

      scrollBar.style.display = isBottom ? 'none' : 'block';
    }), 50);

    this.gridSubscription = resourceSubject('tr-grid').subscribe({
      next: (event) => {
        if (event === 'columnResized') setTimeout(() => resize(), 0);
      },
    });

    return scrollBar;
  },

  initHeader() {
    this.header = this.querySelector('.grid-header');
    this.headerRow = this.header.querySelector('.grid-row');

    this.headerStickyCellStyle = this
      .headerRow.querySelector('.grid-sticky-cell').style;
    this.headerContentStyle = this.querySelector('.grid-header-content').style;

    this.querySelector('.grid-header-placeholder')
      .style.height = `${this.header.getBoundingClientRect().height}px`;
  },

  getVerticalContainerTop() {
    if (this.verticalContainerTop == null) {
      this.verticalContainerTop = this
        .verticalContainer.getBoundingClientRect().top;
    }
    return this.verticalContainerTop;
  },

  getUnstickyColumnHeaders() {
    if (this.unstickyColumnHeaders == null) {
      this.unstickyColumnHeaders = Array.from(this.headerRow.children)
        .filter(col => col.dataset.columnKey);
    }
    return this.unstickyColumnHeaders;
  },

  getStickyColumnMinWidth() {
    return this.stickyColumnMinWidth;
  },

  calcStickyColumnMaxWidth() {
    let maxWidth = this.getHeaderWidth();

    this.getUnstickyColumnHeaders().forEach((column) => {
      if (!column.classList.contains('hidden')) {
        const w = column.width;
        maxWidth -= w || column.getBoundingClientRect().width;
      }
    });

    const minWidth = this.getStickyColumnMinWidth();
    if (maxWidth < minWidth) {
      maxWidth = minWidth;
    }

    return maxWidth;
  },

  updateHeaderStickyCellWidth(width) {
    this.stickyColumnStyle = this.stickyColumnStyle
      || this.header.querySelector('.grid-sticky-cell').style;
    this.stickyColumnPlaceholderStyle = this.stickyColumnPlaceholderStyle
      || this.header.querySelector('.grid-sticky-cell-placeholder').style;

    this.stickyColumnStyle.width = `${width}px`;
    this.stickyColumnPlaceholderStyle.width = `${width}px`;
    const w = width - 1;
    this.horizontalScrollSeam.style.width = `${w}px`;
  },

  updateStickyColumnRange({ max, min } = {}) {
    const maxW = max || this.calcStickyColumnMaxWidth();
    const minW = min || this.getStickyColumnMinWidth();

    this.resizableStickyColumnHeader.min = minW;
    if (maxW === minW) {
      this.resizableStickyColumnHeader.max = maxW * 1.3;
    } else {
      this.resizableStickyColumnHeader.max = maxW;
    }
    this.broadcastSubjectEvent('stickyColumnRangeChagned', {
      max: this.resizableStickyColumnHeader.max, min: minW,
    });
  },

  getHeaderWidth() {
    const horizontalContainerWidth = this
      .horizontalContainer.getBoundingClientRect().width;
    const {
      paddingLeft, paddingRight,
    } = window.getComputedStyle(this.headerRow, null);

    return horizontalContainerWidth
      - parseFloat(paddingLeft) - parseFloat(paddingRight);
  },

  adjustHeaderScrollLeft() {
    if (!this.headerSticky
      || this.header.classList.contains('horizontal-scrolling')) {
      this.headerRow.style.marginLeft = '';
    } else {
      this.headerRow.style.marginLeft = `-${
        this.horizontalContainer.scrollLeft
      }px`;
    }
  },

  disconnected() {
    this.stopListening();
    if (this.gridSubscription) this.gridSubscription.unsubscribe();
  },
};
